import { useParams, Link, useLocation, Outlet } from "react-router-dom";
import { PropsWithChildren, useEffect, useMemo } from 'react';
import { Alert, ConfigProvider, Layout, Menu, Spin } from 'antd';
import styled from 'styled-components';
import { MaintenanceBanner, TempProvider, useTemp } from 'shared';
import { PropSettingsProvider, usePropSettings } from '../../utils/prop-settings-channel';
import { localAppUrl } from '../../utils/local-app';

const { Header, Content } = Layout;

const FullScreenLayout = styled(Layout)`
  min-height: 100vh;
	min-width: 100vw;
	background: none;
`;

const FlexHeader = styled(Header)`
  display: flex;
  align-items: center;
  padding: 0;
  background-color: rgb(0, 0, 0, 0.4);
	backdrop-filter: blur(16px);
`;

const StretchMenu = styled(Menu)`
  flex-grow: 1;
`;

const PropTempUnits = ({ children }: PropsWithChildren) => {
  const { settings } = usePropSettings();
  const { usesCelcius, setUsesCelcius } = useTemp();

  useEffect(() => {
    if (settings && usesCelcius !== settings.use_celcius) {
      setUsesCelcius(settings.use_celcius ?? true);
    }
  }, [settings, usesCelcius, setUsesCelcius]);

  if (!settings || usesCelcius !== (settings.use_celcius ?? true)) {
    return <Spin style={{ display: 'block', margin: '32px auto' }} />;
  }

  return <>{children}</>;
};

const PropMaintenanceBanner = () => {
  const { settings } = usePropSettings();
  return <MaintenanceBanner on={settings?.maintenance} />;
};

const PropOfflineBanner = () => {
  const { settings } = usePropSettings();
  return settings?.online === false
    ? <Alert banner type="warning" message={`${settings.name} is offline. Nothing here can be changed until it reconnects.`} />
    : null;
};

// the unit is the source of truth, so changes wait until it's back to hear
// them; the tabs stay usable to look around
const PropOfflineDisabled = ({ children }: PropsWithChildren) => {
  const { settings } = usePropSettings();
  return <ConfigProvider componentDisabled={settings?.online === false}>{children}</ConfigProvider>;
};

// the prop's tabs, plus a link out to the unit's own app on its LAN address,
// which only opens from a browser on the same network as the unit
const PropMenu = () => {
  const { prop_id } = useParams();
  const { settings } = usePropSettings();
  const localIp = settings?.local_ip;

  const pages = useMemo(() => [
    { name: 'Schedules', path: `/props/${prop_id}/schedules` },
    { name: 'Tasks', path: `/props/${prop_id}/tasks` },
    { name: 'Control', path: `/props/${prop_id}/control` },
    { name: 'Settings', path: `/props/${prop_id}/settings` },
    { name: 'Sensors', path: `/props/${prop_id}/sensors` },
  ], [prop_id]);

	const location = useLocation();

	const selectedMenuKeys = useMemo(() => pages
		.filter(({ path }) => location.pathname.startsWith(path))
		.map(({ name }) => name)
		, [location.pathname, pages])

	const menu = useMemo(() => [
		...pages.map(({ name, path }) => ({
			key: name,
			label: <Link to={path}>{name}</Link>
		})),
		...(localIp ? [{
			key: 'local-app',
			label: <a href={localAppUrl(localIp)} target="_blank" rel="noreferrer" title={`Opens ${localIp}; only reachable on the unit's local network`}>Direct Link ↗</a>,
		}] : []),
	], [pages, localIp]);

	return <StretchMenu mode='horizontal' items={menu} selectedKeys={selectedMenuKeys} />;
};

export const PropFrame = () => {
	// settings wrap the whole frame so the banner and tabs can read them
	return <PropSettingsProvider>
		<FullScreenLayout>
			<PropOfflineBanner />
			<PropMaintenanceBanner />
			<FlexHeader>
				<PropMenu />
			</FlexHeader>
			<Content>
				<TempProvider>
					<PropTempUnits>
						<PropOfflineDisabled>
							<Outlet />
						</PropOfflineDisabled>
					</PropTempUnits>
				</TempProvider>
			</Content>
		</FullScreenLayout>
	</PropSettingsProvider>
};
